import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Spinner, Alert, Button } from 'react-bootstrap';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { USER_ENDPOINTS, SUBSCRIPTION_ENDPOINTS, CONTENT_ENDPOINTS } from '../utils/constants';

const CheckoutSuccess = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Extract reference from URL query parameters
    const params = new URLSearchParams(location.search);
    const reference = params.get('reference') || params.get('trxref');
    
    // Get stored reference and plan ID from session storage
    const storedRef = sessionStorage.getItem('paymentRef');
    const planId = sessionStorage.getItem('planId');
    const nextPayDate = sessionStorage.getItem('nextPayDate');
    
    
    // Verify payment when component mounts
    if (reference || storedRef) {
      verifyPayment(reference || storedRef, planId, nextPayDate);
    } else {
      setLoading(false);
      setError('No payment reference found');
    }
  }, [location]);

  // Verify payment with the server
  const verifyPayment = async (reference, planId, nextPayDate) => {
    try {
      // Get token from session storage (try both possible keys)
      const token = sessionStorage.getItem('userToken') || sessionStorage.getItem('authToken');
      
      if (!token) {
        console.error('CheckoutSuccess - No token found');
        setError('Authentication token not found. Please log in again.');
        setLoading(false);
        return;
      }

      // Call the verify-payment endpoint
      const response = await axios.post(
        SUBSCRIPTION_ENDPOINTS.VERIFY_PAYMENT,
        {
          reference,
          planId,
          newNextPayDate: nextPayDate
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );


      if (response.data.status === 'success') {
        // Check if this was a plan change
        const isPlanChange = sessionStorage.getItem('isPlanChange') === 'true';
        
        
        // If this was a plan change, update the subscription plan
        if (isPlanChange && planId) {
          try {
            // Get user email from userDetails
            const userDetails = JSON.parse(sessionStorage.getItem('userDetails') || '{}');
            const userEmail = userDetails.Email;
            
            if (!userEmail) {
              console.error('CheckoutSuccess - No user email found');
              throw new Error('User email not found');
            }


            await axios.post(
              SUBSCRIPTION_ENDPOINTS.UPDATE_PLAN,
              {
                email: userEmail,
                planId
              },
              {
                headers: {
                  'Authorization': `Bearer ${token}`
                }
              }
            );

          } catch (updateError) {
            console.error('Error updating subscription plan:', updateError);
            // Continue with success message even if update fails, as payment was successful
          }
        }
        
        setPaymentDetails(response.data);
        
        // Clean up session storage
        sessionStorage.removeItem('paymentRef');
        sessionStorage.removeItem('planId');
        sessionStorage.removeItem('nextPayDate');
        sessionStorage.removeItem('isPlanChange');

      } else {
        console.error('CheckoutSuccess - Payment verification failed:', response.data);
        setError('Payment verification failed. Please contact support.');
      }
    } catch (error) {
      console.error('CheckoutSuccess - Error verifying payment:', error);
      if (error.response) {
        console.error('CheckoutSuccess - Error response:', error.response.data);
        setError(`Payment verification failed: ${error.response.data.message || error.response.statusText}`);
      } else {
        setError('An error occurred while verifying payment. Please contact support.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoToProfile = () => {
    // Store success message for Profile component
    sessionStorage.setItem('paymentSuccess', 'true');
    navigate('/profile');
  };

  return (
    <Container className="py-5">
      <Row className="justify-content-center">
        <Col md={8} lg={6}>
          <Card className="shadow-sm">
            <Card.Body className="text-center p-5">
              {loading ? (
                <>
                  <Spinner animation="border" variant="primary" className="mb-3" />
                  <h3 className="mb-3">Verifying Payment</h3>
                  <p className="text-muted">Please wait while we verify your payment...</p>
                </>
              ) : error ? (
                <>
                  <div className="mb-4 text-danger">
                    <i className="fas fa-times-circle fa-4x"></i>
                  </div>
                  <h3 className="mb-3">Payment Verification Failed</h3>
                  <Alert variant="danger">{error}</Alert>
                  <div className="mt-3">
                    <Button 
                      variant="primary" 
                      className="me-2"
                      onClick={handleGoToProfile}
                    >
                      Go to Profile
                    </Button>
                    <Button 
                      variant="secondary"
                      onClick={() => navigate('/login')}
                    >
                      Login Again
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <div className="mb-4 text-success">
                    <i className="fas fa-check-circle fa-4x"></i>
                  </div>
                  <h3 className="mb-3">Payment Successful!</h3>
                  <p className="text-muted mb-4">
                    Your payment has been processed successfully. Your subscription has been updated.
                  </p>
                  
                  {paymentDetails && (
                    <div className="payment-details text-start bg-light p-3 rounded mb-4">
                      <h5 className="border-bottom pb-2 mb-3">Payment Details</h5>
                      <p className="mb-2">
                        <strong>Plan:</strong> {paymentDetails.planName}
                      </p>
                      <p className="mb-2">
                        <strong>Amount:</strong> {paymentDetails.data?.currency || 'ZAR'} {paymentDetails.data?.amount ? (paymentDetails.data.amount / 100).toFixed(2) : 'N/A'}
                      </p>
                      <p className="mb-2">
                        <strong>Next Payment Date:</strong> {paymentDetails.nextPayDate ? new Date(paymentDetails.nextPayDate).toLocaleDateString() : 'N/A'}
                      </p>
                      <p className="mb-0">
                        <strong>Transaction Reference:</strong> {paymentDetails.data?.reference || 'N/A'}
                      </p>
                    </div>
                  )}
                  
                  <Button 
                    variant="success" 
                    size="lg"
                    onClick={handleGoToProfile}
                  >
                    <i className="fas fa-user me-2"></i>
                    Go to My Profile
                  </Button>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default CheckoutSuccess;